import * as Sentry from '@sentry/nuxt'
import { getHttpStatus, getShopwareErrorCodes } from '@/utils/helper/checkoutError'

export type CheckoutErrorStep = 'placeOrder' | 'handlePayment' | 'resetPayment' | 'paymentFinalize'

export interface CheckoutErrorContext {
    /** Technical payment method code; leave empty when the session no longer reflects the order. */
    paymentMethodCode?: string
    /** Error code known to the caller when `e` carries no Shopware codes (e.g. the `error-code` query). */
    errorCode?: string
}

/**
 * Reports a failed checkout step to Sentry as an exception.
 *
 * Only the step, the payment method's technical code, the HTTP status and Shopware error codes
 * are attached - never request/response bodies, which carry addresses and order comments.
 * A no-op while Sentry is not initialised (empty DSN).
 */
export function reportCheckoutError (step: CheckoutErrorStep, e: unknown, context: CheckoutErrorContext = {}): void {
    const shopwareCodes = getShopwareErrorCodes(e)
    const codes = shopwareCodes.length > 0 || context.errorCode == null ? shopwareCodes : [context.errorCode]

    Sentry.withScope((scope) => {
        scope.setTag('checkout.step', step)
        scope.setTag('checkout.payment_method', context.paymentMethodCode ?? 'unknown')
        scope.setTag('checkout.error_code', codes[0] ?? 'none')
        scope.setExtras({ status: getHttpStatus(e), codes })

        Sentry.captureException(e instanceof Error ? e : new Error(`Checkout ${step} failed`))
    })
}
